/**
 * UsePendingApprovals Hook
 *
 * Fetches pending approvals assigned to the current user.
 * Requires backend enhancement (GET /approvals/pending).
 * Returns empty list with graceful degradation if endpoint not available.
 */

import {useQuery} from '@tanstack/react-query';
import {getResourceApi} from '@/matic/features/vault/api/resource-api';
import {useApiToken} from '@/matic/hooks/useApiClient';
import {useAppContext} from '@/matic/providers/AppProvider';
import type {ApprovalList} from '../types';

/**
 * Fetch pending approvals for current user
 *
 * NOTE: Requires backend enhancement (GET /approvals/pending)
 * Returns empty list if endpoint not available (graceful degradation)
 *
 * @param tenantId - Tenant ID (UUID)
 * @param options - Query options (limit, offset)
 * @returns Query result with approval list
 *
 * @example
 * ```typescript
 * const { data: pending } = usePendingApprovals(tenantId);
 *
 * <Badge count={pending?.total || 0}>
 *   Pending Approvals
 * </Badge>
 * ```
 */
export function usePendingApprovals(
  tenantId: string,
  options: {limit?: number; offset?: number} = {}
) {
  const {userId} = useAppContext();
  const token = useApiToken();
  const api = getResourceApi(token);

  return useQuery<ApprovalList>({
    queryKey: ['approvals', 'pending', userId, tenantId, options],
    queryFn: async () => api.getPendingApprovals(tenantId, options),
    enabled: Boolean(userId) && Boolean(tenantId),
    // Refetch frequently (user might be assigned new approvals)
    refetchInterval: 30 * 1000, // Every 30 seconds
    refetchOnWindowFocus: true,
    staleTime: 0, // Always consider stale (refetch on use)
  });
}
